import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, Users, CheckCircle, AlertTriangle,
  Plus, RefreshCw, BarChart3, DollarSign, Target, Calendar, MousePointerClick
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import MetricDrillDown from '../../components/MetricDrillDown';

export default function RecidivismTrackingPage() {
  const { axiosAuth } = useAuth();
  const [timeframe, setTimeframe] = useState('all');
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [newRecord, setNewRecord] = useState({
    participant_id: '',
    outcome_type: 'success',
    outcome_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const api = axiosAuth();

  const openDrillDown = async (metric) => {
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const res = await api.get(`/reports/recidivism/breakdown/${metric}?timeframe=${timeframe}`);
      setDrillData(res.data);
    } catch (err) {
      toast.error('Failed to load breakdown');
      setDrillOpen(false);
    }
    setDrillLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, recordsRes] = await Promise.all([
        api.get(`/reports/recidivism/summary?timeframe=${timeframe}`),
        api.get('/reports/recidivism/records')
      ]);
      setSummary(summaryRes.data);
      setRecords(recordsRes.data || []);
    } catch (err) {
      toast.error('Failed to load recidivism data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  const handleAddRecord = async () => {
    if (!newRecord.participant_id) {
      toast.error('Please enter a participant ID');
      return;
    }
    try {
      await api.post(`/reports/recidivism/track?participant_id=${newRecord.participant_id}&outcome_type=${newRecord.outcome_type}&outcome_date=${newRecord.outcome_date}&notes=${encodeURIComponent(newRecord.notes)}`);
      toast.success('Outcome tracked successfully');
      setAddDialogOpen(false);
      setNewRecord({ participant_id: '', outcome_type: 'success', outcome_date: new Date().toISOString().split('T')[0], notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to track outcome');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recidivism-tracking-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Recidivism Tracking
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Track post-program outcomes and measure long-term success
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6month">6 Months</SelectItem>
              <SelectItem value="1year">1 Year</SelectItem>
              <SelectItem value="3year">3 Years</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2">
            <Plus size={16} /> Track Outcome
          </Button>
        </div>
      </div>

      {summary && (
        <>
          {/* Success Rate Hero */}
          <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <p className="text-violet-100 text-sm font-medium mb-2">PROGRAM SUCCESS RATE</p>
                  <p className="text-5xl md:text-6xl font-bold">{summary.rates.success_rate}%</p>
                  <p className="text-violet-100 mt-2">
                    vs. National Average: {100 - summary.rates.national_average_recidivism}% success
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-violet-200 text-sm">Our Recidivism</p>
                    <p className="text-3xl font-bold">{summary.rates.recidivism_rate}%</p>
                  </div>
                  <div className="text-4xl">vs</div>
                  <div className="text-center">
                    <p className="text-violet-200 text-sm">National Avg</p>
                    <p className="text-3xl font-bold">{summary.rates.national_average_recidivism}%</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Stats — click to drill down */}
          <p className="text-xs text-muted-foreground flex items-center gap-1 -mb-2"><MousePointerClick size={11} /> Tap any card to see participants and methodology</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card onClick={() => openDrillDown('success_rate')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-successes">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle className="text-emerald-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{summary.outcomes.successes}</p>
                <p className="text-xs text-muted-foreground">Successful Outcomes</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('recidivism_rate')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-recidivated">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                    <AlertTriangle className="text-red-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-red-600">{summary.outcomes.recidivated}</p>
                <p className="text-xs text-muted-foreground">Recidivated</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('pending_follow_up')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-pending-followup">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <TrendingUp className="text-violet-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-violet-600">+{summary.rates.improvement_vs_national}%</p>
                <p className="text-xs text-muted-foreground">Better Than National</p>
              </CardContent>
            </Card>
            <Card onClick={() => openDrillDown('prevented_reincarcerations')} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid="card-savings-from-successes">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <DollarSign className="text-amber-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.cost_impact.estimated_savings)}</p>
                <p className="text-xs text-muted-foreground">Savings from Successes</p>
              </CardContent>
            </Card>
          </div>

          {/* Tracking Coverage & Comparison */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Tracking Coverage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm">Tracked vs Total Completers</span>
                    <span className="text-sm font-medium">{summary.tracking_summary.tracking_coverage}%</span>
                  </div>
                  <Progress value={summary.tracking_summary.tracking_coverage} className="h-3" />
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="text-center p-3 bg-muted/30 rounded-xl">
                    <p className="text-xl font-bold">{summary.tracking_summary.total_program_completers}</p>
                    <p className="text-xs text-muted-foreground">Completers</p>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-xl">
                    <p className="text-xl font-bold">{summary.tracking_summary.total_tracked_outcomes}</p>
                    <p className="text-xs text-muted-foreground">Tracked</p>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-xl">
                    <p className="text-xl font-bold">{summary.outcomes.pending_follow_up}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Rate Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Our Program</p>
                    <div className="h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white font-bold" 
                         style={{width: `${Math.max(20, 100 - summary.rates.recidivism_rate)}%`}}>
                      {summary.rates.success_rate}% Success
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">National Average</p>
                    <div className="h-8 bg-red-400 rounded-lg flex items-center justify-center text-white font-bold"
                         style={{width: `${Math.max(20, summary.rates.national_average_recidivism)}%`}}>
                      {summary.rates.national_average_recidivism}% Recidivism
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-emerald-700 font-medium">
                    Our participants are <span className="text-lg font-bold">{summary.rates.improvement_vs_national}%</span> less likely to recidivate
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Records */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                Outcome Records
              </CardTitle>
              <Button onClick={fetchData} variant="ghost" size="sm" className="gap-2">
                <RefreshCw size={14} /> Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {records.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No outcome records yet.</p>
                  <Button onClick={() => setAddDialogOpen(true)} variant="outline" className="mt-3 rounded-full">
                    Track First Outcome
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">Participant</th>
                        <th className="text-left py-2 px-3 font-medium">Outcome</th>
                        <th className="text-left py-2 px-3 font-medium">Date</th>
                        <th className="text-left py-2 px-3 font-medium">Tracked By</th>
                        <th className="text-left py-2 px-3 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.slice(0, 20).map((record, idx) => (
                        <tr key={`item-${idx}`} className="border-b hover:bg-muted/30">
                          <td className="py-2.5 px-3 font-medium">{record.participant_name}</td>
                          <td className="py-2.5 px-3">
                            <Badge className={record.outcome_type === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {record.outcome_type === 'success' ? <CheckCircle size={12} className="mr-1" /> : <AlertTriangle size={12} className="mr-1" />}
                              {record.outcome_type}
                            </Badge>
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground">{formatDate(record.outcome_date)}</td>
                          <td className="py-2.5 px-3">{record.tracked_by_name}</td>
                          <td className="py-2.5 px-3 text-muted-foreground max-w-xs truncate">{record.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add Record Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Track Post-Program Outcome</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Participant ID</label>
              <Input 
                value={newRecord.participant_id}
                onChange={(e) => setNewRecord({ ...newRecord, participant_id: e.target.value })}
                placeholder="Enter participant ID"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Outcome Type</label>
              <Select value={newRecord.outcome_type} onValueChange={(v) => setNewRecord({ ...newRecord, outcome_type: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="success">Success (No Recidivism)</SelectItem>
                  <SelectItem value="recidivated">Recidivated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Outcome Date</label>
              <Input 
                type="date"
                value={newRecord.outcome_date}
                onChange={(e) => setNewRecord({ ...newRecord, outcome_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes (optional)</label>
              <DictationInput 
                value={newRecord.notes}
                onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                placeholder="Add any relevant notes..."
                className="rounded-xl"
                rows={3}
              showCleanup={false} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddRecord} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2">
              <Plus size={16} /> Track Outcome
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MetricDrillDown open={drillOpen} onOpenChange={setDrillOpen} data={drillData} loading={drillLoading} />
    </div>
  );
}
